import { useState } from 'react';
import type { Sprint } from '../../api/types';
import { Notice } from '../../components/ui/Notice';
import type { Version } from '../versions/versions-api';
import {
  NO_VERSION,
  SPRINT_GOAL_MAX,
  SPRINT_NAME_MAX,
  blankDraft,
  draftOf,
  draftProblem,
  type SprintDraft,
} from './sprint-form';

/**
 * CREATE OR EDIT A SPRINT — PH-23's form.
 *
 * 🔴 THE TICKET: *"No way to CREATE or START a sprint in the UI — only close it."* Or went to the
 * backlog on his PHONE to do what he would do in Jira and there was no such control at any width.
 * ⇒ This is the create half; `useStartSprint` is the start half.
 *
 * 🔴 MOBILE FIRST, 390px — the screen it was reported from. Same shape as `CompleteSprintDialog` and
 * `PromoteDialog`, deliberately: a BOTTOM SHEET on a phone (`items-end`, rounded top corners, full
 * width) and a centred dialog from `sm:` up. Every field is 44px tall, the two date inputs STACK
 * below `sm:` rather than sharing a row at 180px each, and the buttons are full-width-stacked with
 * the primary one nearest the thumb. ⛔ A centred card at 390px puts the confirm button in the middle
 * of the screen, which is where nobody's thumb is.
 *
 * ⭐ THE DRAFT LIVES HERE, not in the hook — the same division `CompleteSprintDialog` makes with its
 * carry-over `target`. The hook owns the WRITE and its refusal; the dialog owns what is on the form
 * until somebody presses Save. That is why a failed save leaves every field exactly as typed.
 *
 * ⛔ SUBMIT VALIDATES AND CAN WRITE NOTHING, and the button is deliberately NOT disabled to achieve
 * that. A greyed-out Save with no explanation is the "it did nothing" failure this codebase refuses;
 * a pressed Save that answers `A sprint needs a name.` tells the reader what to do next. The guard is
 * therefore reachable through the UI, which is the only kind of guard a test can prove.
 */

export type SprintFormMode = { kind: 'create' } | { kind: 'edit'; sprint: Sprint };

/**
 * What we can offer in the version picker.
 *
 * ⛔ THE MISSING CASE IS MODELLED RATHER THAN FAKED. `GET /projects/:id/versions` answers
 * `no such route` on a deployment older than the commit that added it, and `versions-api.ts` already
 * tells that apart from a real 404. A field that silently shows "No version" for both "this project
 * has none" and "we could not ask" is a fixture kinder than the API, which is how the last defect
 * here reached production.
 */
export type VersionChoices =
  | { kind: 'loading' }
  | { kind: 'ok'; versions: readonly Version[] }
  | { kind: 'unavailable'; reason: string };

const FIELD_CLASS =
  'mt-1 h-11 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 text-sm disabled:opacity-50';
const LABEL_CLASS = 'block text-xs font-semibold';

export function SprintFormDialog({
  mode,
  today,
  versions,
  busy,
  error,
  problem: outerProblem = null,
  onCancel,
  onSubmit,
}: {
  mode: SprintFormMode;
  /** `YYYY-MM-DD`. ⭐ Passed in rather than read here, so a test can pin the day. */
  today: string;
  versions: VersionChoices;
  busy: boolean;
  /** The API's own words for a refusal — something WAS sent. */
  error: string | null;
  /**
   * A refusal from the CALLER that never reached the API — today, "nothing has changed". PH-60.
   *
   * ⛔ It joins this dialog's OWN `problem` channel rather than `error`, because both mean the same
   * thing to a reader: nothing was sent, and pressing Save again unchanged will do the same. Routing
   * it through `error` put "The API refused this sprint" above a request the API never saw.
   */
  problem?: string | null;
  onCancel: () => void;
  onSubmit: (draft: SprintDraft) => void;
}) {
  const editing = mode.kind === 'edit' ? mode.sprint : null;
  // ⭐ Initialised ONCE per open. The dialog is unmounted between opens (the page renders it only
  //   while a mode is set), so there is no stale draft to reset and no effect to write one.
  const [draft, setDraft] = useState<SprintDraft>(() =>
    editing ? draftOf(editing) : blankDraft(today),
  );
  const [ownProblem, setOwnProblem] = useState<string | null>(null);
  // ⭐ OURS wins while set: it names a field the reader can fix now, whereas the caller's is about
  //   the form as a whole. Both render in the same place under the same heading.
  const problem = ownProblem ?? outerProblem;

  const set = <K extends keyof SprintDraft>(key: K, value: SprintDraft[K]) => {
    // Immutable, and it clears our own complaint: a message about a field the user has since fixed
    // is a message that reads as a fresh refusal.
    setDraft((current) => ({ ...current, [key]: value }));
    setOwnProblem(null);
  };

  const submit = () => {
    const found = draftProblem(draft);
    setOwnProblem(found);
    // ⛔ THE ASSERTION THIS EXISTS FOR: an invalid draft writes NOTHING. It does not go to the API to
    //    be refused with a generic `the request body or query was not valid`.
    if (found) return;
    onSubmit(draft);
  };

  const title = editing ? `Edit ${editing.name}` : 'New sprint';
  const submitLabel = editing ? 'Save sprint' : 'Create sprint';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-full w-full max-w-lg space-y-3 overflow-y-auto rounded-t-lg border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-4 sm:rounded-lg"
      >
        <h2 className="text-base font-semibold">{title}</h2>

        {/* ⭐ Stated for CREATE only, and it is the thing PH-23's reporter needs to know next: the
            sprint he is about to make will NOT be the one the team is working in until he starts it.
            ⛔ Not a hint under the button — the API makes `planned` unavoidable (`createSprintBody`
            has no `state`), so this is a fact about what is about to happen. */}
        {editing ? null : (
          <p className="text-xs text-[color:var(--color-ink-muted)]">
            A new sprint is created <strong>Planned</strong>. It becomes the team's active sprint only
            when you press <strong>Start sprint</strong> on it — and only one sprint per project may
            be active at a time.
          </p>
        )}

        <div>
          <label className={LABEL_CLASS} htmlFor="sprint-form-name">
            Name
          </label>
          <input
            id="sprint-form-name"
            type="text"
            value={draft.name}
            maxLength={SPRINT_NAME_MAX}
            onChange={(event) => set('name', event.target.value)}
            disabled={busy}
            placeholder="APFS v1.0.35"
            className={FIELD_CLASS}
          />
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            Unique within this project — the API answers a duplicate with{' '}
            <em>a sprint named '…' already exists in this project</em>.
          </p>
        </div>

        {/* 🔴 STACKED AT 390px, side by side from `sm:`. Two date inputs sharing a phone row leaves
            each about 170px wide, and a native date picker in that space is a control people mis-tap.
            ⛔ And these are `type="date"`, which reads and writes the API's own `YYYY-MM-DD` — no
            `new Date()` is constructed anywhere between the field and the request body. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={LABEL_CLASS} htmlFor="sprint-form-starts-on">
              Starts on
            </label>
            <input
              id="sprint-form-starts-on"
              type="date"
              value={draft.startsOn}
              onChange={(event) => set('startsOn', event.target.value)}
              disabled={busy}
              className={FIELD_CLASS}
            />
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="sprint-form-ends-on">
              Ends on
            </label>
            <input
              id="sprint-form-ends-on"
              type="date"
              value={draft.endsOn}
              onChange={(event) => set('endsOn', event.target.value)}
              disabled={busy}
              className={FIELD_CLASS}
            />
          </div>
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="sprint-form-goal">
            Goal <span className="font-normal text-[color:var(--color-ink-muted)]">(optional)</span>
          </label>
          <textarea
            id="sprint-form-goal"
            value={draft.goal}
            maxLength={SPRINT_GOAL_MAX}
            onChange={(event) => set('goal', event.target.value)}
            disabled={busy}
            rows={2}
            className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 text-sm disabled:opacity-50"
          />
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="sprint-form-version">
            Release{' '}
            <span className="font-normal text-[color:var(--color-ink-muted)]">(optional)</span>
          </label>
          <select
            id="sprint-form-version"
            value={draft.versionId}
            onChange={(event) => set('versionId', event.target.value)}
            disabled={busy || versions.kind !== 'ok'}
            className={FIELD_CLASS}
          >
            <option value={NO_VERSION}>No release</option>
            {versions.kind === 'ok'
              ? versions.versions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {version.name}
                  </option>
                ))
              : null}
          </select>
          {/* ⛔ "We could not ask" is said out loud rather than rendered as an empty list. An empty
              picker that means "this project has no releases" and one that means "the endpoint is
              not deployed" are different facts and lead to different actions. */}
          {versions.kind === 'unavailable' ? (
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              Releases could not be loaded ({versions.reason}) — the sprint will be saved without one.
              This is not a claim that the project has none.
            </p>
          ) : null}
          {versions.kind === 'ok' && versions.versions.length === 0 ? (
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              This project has no releases yet.
            </p>
          ) : null}
        </div>

        {/* ⚠️ TWO SOURCES OF "NO", KEPT APART. `problem` is OUR pre-flight refusal and nothing was
            sent; `error` is the API's and something was. A reader who cannot tell them apart cannot
            tell whether pressing Save again might work. */}
        {problem ? (
          <Notice tone="warning" title="This sprint was not sent">
            <p className="break-words">{problem}</p>
          </Notice>
        ) : null}

        {error ? (
          <Notice tone="error" title="The API refused this sprint">
            <p className="break-words">{error}</p>
          </Notice>
        ) : null}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="h-11 rounded border border-[color:var(--color-line)] px-3 text-sm disabled:opacity-50 sm:h-9"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="h-11 rounded bg-[color:var(--color-accent)] px-4 text-sm font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50 sm:h-9"
          >
            {busy ? 'Saving…' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
